import React, { useEffect, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import Loader from "../Loader";

type InfinitePaginatedListProps<T> = {
  fetchData: (page: number) => Promise<{
    items: T[];
    hasMore: boolean;
  }>;
  renderItem: (item: T, index: number) => React.ReactNode;
  containerHeight?: string | number;
  startingPage?: number;
  loader?: React.ReactNode;
  emptyMessage?: React.ReactNode;
  scrollableTarget?: string; // Custom scroll container (external)
};

function InfinitePaginatedList<T>({
  fetchData,
  renderItem,
  containerHeight = "400px",
  startingPage = 1,
  loader = <Loader />,
  emptyMessage = (
    <p className="text-center text-sm text-gray-500 mt-4">No results found.</p>
  ),
  scrollableTarget = "scrollable-list",
}: InfinitePaginatedListProps<T>) {
  const [data, setData] = useState<T[]>([]);
  const [page, setPage] = useState(startingPage);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);

  const loadMore = async () => {
    if (!hasMore) return;

    try {
      const res = await fetchData(page);
      setData((prev) => [...prev, ...res.items]);
      setHasMore(res.hasMore);
      setPage((prev) => prev + 1);
    } catch (error) {
      console.error("Error fetching paginated data:", error);
      setHasMore(false);
    }
  };

  // Initial load
  useEffect(() => {
    setPage(startingPage);
    setData([]);
    setHasMore(true);
    setInitialLoading(true);

    fetchData(startingPage)
      .then((res) => {
        setData(res.items);
        setHasMore(res.hasMore);
        setPage(startingPage + 1);
      })
      .catch((err) => {
        console.error("Error during initial fetch:", err);
        setHasMore(false);
      })
      .finally(() => {
        setInitialLoading(false);
      });
  }, [fetchData, startingPage]);

  return (
    <div
      id="scrollable-list"
      style={{
        height: containerHeight,
        overflow: "auto",
      }}
    >
      {initialLoading ? (
        loader
      ) : data.length === 0 ? (
        emptyMessage
      ) : (
        <InfiniteScroll
          dataLength={data.length}
          next={loadMore}
          hasMore={hasMore}
          loader={loader}
          scrollableTarget={scrollableTarget}
          scrollThreshold={0.8}
          className="space-y-4"
        >
          {data.map((item, index) => renderItem(item, index))}
        </InfiniteScroll>
      )}
    </div>
  );
}

export default InfinitePaginatedList;
